import React, { useState, useEffect, useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import { AnalysisFilterSection, LocationOption } from '@popety-io/ui'

import { BuildingPermitsDataTable } from './BuildingPermitsDataTable'
import { buildingPermitsService } from '../../services/buildingPermitsService'
import type {
  AnalysisData,
  BuildingPermit,
  PermitFilters,
  PermitSortOptions,
} from '@popety-io/types'

// Map section data context interface for optional prop injection
interface MapSectionDataContextFunctions {
  updateSectionData: (data: Record<string, any>) => void
  setActiveSection: (section: any) => void
  setHoveredItemId: (id: string | number | null) => void
  setSelectedItemId: (id: string | number | null) => void
  setFlyToCoordinates: (coordinates: [number, number] | null) => void
  setOnItemClick: (handler: ((item: any) => void) | null) => void
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  position: relative;
  overflow: hidden;
`

const TableContainer = styled.div`
  flex: 1;
  overflow: auto;
  padding: ${({ theme }) => theme.spacing.large};
`

interface BuildingPermitsAroundPlotProps {
  locationInfo: {
    postalCode?: string
    city?: string
    district?: string
    municipality?: string
    region?: string
    coordinates?: { lat: number; lon: number }
  } | null
  analysisData?: AnalysisData | null
  onPermitClick?: (permit: BuildingPermit) => void
  onToggleSave?: (permit: BuildingPermit) => void
  savedPermitIds?: string[]
  propertyId?: string
  /** Optional context functions - pass these when using outside of LandAnalysis */
  mapSectionData?: MapSectionDataContextFunctions
}

export const BuildingPermitsAroundPlot: React.FC<
  BuildingPermitsAroundPlotProps
> = ({
  locationInfo,
  analysisData,
  onPermitClick,
  onToggleSave,
  savedPermitIds = [],
  propertyId = '752',
  mapSectionData,
}) => {
  const { t } = useTranslation()
  const [permits, setPermits] = useState<BuildingPermit[]>([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize, setPageSize] = useState(20)

  // Get map section data context functions from props
  // If not provided, use no-op functions (component will work but won't sync with map)
  const {
    updateSectionData = () => {},
    setActiveSection = () => {},
    setHoveredItemId = () => {},
    setSelectedItemId = () => {},
    setFlyToCoordinates = () => {},
    setOnItemClick = () => {},
  } = mapSectionData || {}

  // Filter states
  const [locationType, setLocationType] = useState<
    'postal_code' | 'city' | 'district' | 'municipality' | 'region' | 'radius'
  >('radius')
  const [radius, setRadius] = useState(500)
  const [filters, setFilters] = useState<any>({})
  const [showFilters, setShowFilters] = useState(false)

  // Location options - show actual location names
  const locationOptions: LocationOption[] = useMemo(
    () => [
      ...(locationInfo?.postalCode
        ? [
            {
              value: 'postal_code',
              label: locationInfo.postalCode,
              icon: 'map-pin',
            },
          ]
        : []),
      ...(locationInfo?.city
        ? [{ value: 'city', label: locationInfo.city, icon: 'building-2' }]
        : []),
      ...(locationInfo?.district
        ? [{ value: 'district', label: locationInfo.district, icon: 'map' }]
        : []),
      ...(locationInfo?.region
        ? [{ value: 'region', label: locationInfo.region, icon: 'map-pin' }]
        : []),
      ...(locationInfo?.coordinates
        ? [{ value: 'radius' as const, label: t('buildingPermits.location.radius'), icon: 'target' }]
        : []),
    ],
    [locationInfo, t]
  )

  // Get location label
  const getLocationLabel = useCallback(() => {
    const allLocations = t('buildingPermits.location.allLocations')
    if (!locationInfo) return allLocations

    switch (locationType) {
      case 'postal_code':
        return locationInfo.postalCode || allLocations
      case 'city':
        return locationInfo.city || allLocations
      case 'district':
        return locationInfo.district || allLocations
      case 'municipality':
        return locationInfo.municipality || allLocations
      case 'region':
        return locationInfo.region || allLocations
      case 'radius':
        return `${radius >= 1000 ? radius / 1000 + t('buildingPermits.location.km') : radius + t('buildingPermits.location.m')} ${t('buildingPermits.location.radius').toLowerCase()}`
      default:
        return allLocations
    }
  }, [locationInfo, locationType, radius, t])

  // Build permit filters based on location type
  const permitFilters: PermitFilters = useMemo(() => {
    const baseFilters: PermitFilters = {
      statuses: [],
      categories: [],
    }

    if (locationType === 'radius') {
      baseFilters.radius = radius
    } else {
      if (locationType === 'postal_code' && locationInfo?.postalCode) {
        baseFilters.postalCodes = [locationInfo.postalCode]
      } else if (locationType === 'city' && locationInfo?.city) {
        baseFilters.cities = [locationInfo.city]
      } else if (locationType === 'district' && locationInfo?.district) {
        // District filtering might not be supported, fallback to city
        baseFilters.cities = [locationInfo.city || '']
      } else if (
        locationType === 'municipality' &&
        locationInfo?.municipality
      ) {
        baseFilters.cities = [locationInfo.municipality]
      } else if (locationType === 'region' && locationInfo?.region) {
        // Region filtering might not be supported
        baseFilters.cities = [locationInfo.city || '']
      }
    }

    return baseFilters
  }, [
    locationType,
    radius,
    locationInfo?.postalCode,
    locationInfo?.city,
    locationInfo?.district,
    locationInfo?.municipality,
    locationInfo?.region,
  ])

  const sort: PermitSortOptions = {
    field: 'date',
    direction: 'desc',
  }

  // Fetch building permits
  useEffect(() => {
    const fetchPermits = async () => {
      if (!locationInfo?.coordinates && locationType === 'radius') {
        setLoading(false)
        return
      }

      setLoading(true)
      setError(null)

      try {
        const response = await buildingPermitsService.searchPermits(
          propertyId,
          permitFilters,
          sort,
          currentPage,
          pageSize,
          locationInfo?.coordinates
        )

        setPermits(response.permits)
        setTotal(response.total)
      } catch (err) {
        console.error('Error fetching building permits:', err)
        setError(
          err instanceof Error
            ? err.message
            : t('buildingPermits.messages.fetchError')
        )
      } finally {
        setLoading(false)
      }
    }

    fetchPermits()
  }, [
    propertyId,
    locationInfo?.coordinates?.lat,
    locationInfo?.coordinates?.lon,
    permitFilters,
    currentPage,
    pageSize,
  ])

  // Push building permits data to map section context when permits change
  useEffect(() => {
    // Set active section
    setActiveSection('building_permits')

    // Update section data with current permits
    updateSectionData({
      building_permits: permits,
    })
  }, [permits])

  // Register permit click handler in context
  useEffect(() => {
    const wrappedHandler = (permit: BuildingPermit) => {
      if (onPermitClick) {
        onPermitClick(permit)
      }
      setSelectedItemId(permit.id)
    }
    setOnItemClick(() => wrappedHandler)
    return () => {
      setOnItemClick(null)
    }
  }, [onPermitClick, setOnItemClick, setSelectedItemId])

  // Handle row hover - highlight the marker on map
  const handleRowHover = useCallback(
    (permit: BuildingPermit | null) => {
      setHoveredItemId(permit?.id || null)
    },
    [setHoveredItemId]
  )

  // Helper to extract coordinates from permit
  const getPermitCoordinates = useCallback(
    (permit: BuildingPermit): [number, number] | null => {
      // Try location field first (GeoJSON format)
      if (permit.location) {
        const loc = permit.location as any
        if (
          loc.type?.toLowerCase() === 'multipoint' &&
          Array.isArray(loc.coordinates) &&
          loc.coordinates.length > 0
        ) {
          const firstPoint = loc.coordinates[0]
          if (Array.isArray(firstPoint) && firstPoint.length >= 2) {
            return [firstPoint[0], firstPoint[1]]
          }
        }
      }

      // Try geo_center field (GeoPoint format)
      if (permit.geo_center) {
        const gc = permit.geo_center as any
        // Object format: {lat, lon}
        if (typeof gc === 'object' && gc.lat && gc.lon) {
          return [gc.lon, gc.lat]
        }
        // String format: "POINT(lon lat)"
        if (typeof gc === 'string') {
          const match = gc.match(/POINT\s*\(\s*([-\d.]+)\s+([-\d.]+)\s*\)/i)
          if (match) {
            return [parseFloat(match[1]), parseFloat(match[2])]
          }
        }
      }

      return null
    },
    []
  )

  // Handle row click - select permit and fly to its location on map
  const handleRowClick = useCallback(
    (permit: BuildingPermit) => {
      // Call the original onPermitClick handler to open detail panel
      if (onPermitClick) {
        onPermitClick(permit)
      }

      // Set the selected permit ID for map highlighting
      setSelectedItemId(permit.id)

      // Fly to permit location if coordinates available
      const coords = getPermitCoordinates(permit)
      if (coords) {
        setFlyToCoordinates(coords)
      }
    },
    [
      onPermitClick,
      setSelectedItemId,
      setFlyToCoordinates,
      getPermitCoordinates,
    ]
  )

  // Handle permit selection - delegates to parent via handleRowClick
  const handlePermitSelect = (permit: BuildingPermit) => {
    handleRowClick(permit)
  }

  // Handle location type change
  const handleLocationTypeChange = useCallback((newType: string) => {
    setLocationType(
      newType as
        | 'postal_code'
        | 'city'
        | 'district'
        | 'municipality'
        | 'region'
        | 'radius'
    )
    setCurrentPage(1)
  }, [])

  // Handle radius change
  const handleRadiusChange = useCallback((newRadius: number) => {
    setRadius(newRadius)
    setCurrentPage(1)
  }, [])

  // Handle clear all filters
  const handleClearAllFilters = useCallback(() => {
    setFilters({})
  }, [])

  // Calculate active filters count
  const activeFiltersCount = useMemo(
    () =>
      Object.keys(filters).filter(
        (key) =>
          filters[key] !== undefined &&
          filters[key] !== null &&
          filters[key] !== ''
      ).length,
    [filters]
  )

  if (error) {
    return (
      <Container>
        <div style={{ padding: '40px', textAlign: 'center' }}>
          <p style={{ color: '#ef4444' }}>Error: {error}</p>
        </div>
      </Container>
    )
  }

  return (
    <Container>
      {/* Filter Section - Using the same AnalysisFilterSection as transactions */}
      <AnalysisFilterSection
        locationType={locationType}
        onLocationTypeChange={handleLocationTypeChange}
        locationOptions={locationOptions}
        locationLabel={getLocationLabel()}
        radius={radius}
        onRadiusChange={handleRadiusChange}
        showRadiusSelector={locationType === 'radius'}
        showFilters={showFilters}
        onToggleFilters={() => setShowFilters(!showFilters)}
        activeFiltersCount={activeFiltersCount}
        onClearAll={handleClearAllFilters}
        t={t}
      >
        {/* TODO: Add building permit specific filters here if needed */}
        {showFilters && (
          <div style={{ padding: '16px' }}>
            <p style={{ color: '#6b7280' }}>
              {t('buildingPermits.messages.advancedFiltersComingSoon')}
            </p>
          </div>
        )}
      </AnalysisFilterSection>

      <TableContainer>
        <BuildingPermitsDataTable
          permits={permits}
          total={total}
          loading={loading}
          onPermitSelect={handlePermitSelect}
          onRowHover={handleRowHover}
          onToggleSave={onToggleSave}
          savedPermitIds={savedPermitIds}
          context="analysis"
          currentPage={currentPage}
          pageSize={pageSize}
          onPageChange={setCurrentPage}
          onPageSizeChange={setPageSize}
        />
      </TableContainer>
    </Container>
  )
}
